import { useMutation, useQueryClient } from "@tanstack/react-query";
import { HelpCircle, PartyPopper, XCircle } from "lucide-react";
import { toast } from "sonner";
import { eventApi } from "~/lib/api/event";
import type { RsvpRequest } from "~/lib/api/types";

export function useRsvp(eventId: string) {
	const queryClient = useQueryClient();

	const { mutateAsync: submitRsvp, isPending } = useMutation({
		mutationFn: (data: RsvpRequest) => eventApi.submitRsvp(eventId, data),
		onSuccess: ({ data }) => {
			// Invalidate event data to trigger a refetch
			queryClient.invalidateQueries({
				queryKey: ["event", eventId],
			});

			if (data.status === "accepted") {
				toast.success(
					<div className="flex items-center gap-3">
						<div className="p-2 rounded-full bg-accent/10">
							<PartyPopper className="w-5 h-5 text-green-500" />
						</div>
						<div className="flex flex-col gap-0.5">
							<p className="font-medium">You're in! 🎉</p>
							<p className="text-sm text-white/80">{`RSVP confirmed${data.bringingPlusOne ? " with a plus one" : ""}`}</p>
						</div>
					</div>,
					{ icon: null },
				);
			} else if (data.status === "maybe") {
				toast.warning(
					<div className="flex items-center gap-3">
						<div className="p-2 rounded-full bg-accent/10">
							<HelpCircle className="w-5 h-5 text-amber-500" />
						</div>
						<div className="flex flex-col gap-0.5">
							<p className="font-medium">Marked as maybe</p>
							<p className="text-sm text-white/80">
								You might attend this event
							</p>
						</div>
					</div>,
					{ icon: null },
				);
			} else if (data.status === "declined") {
				toast.error(
					<div className="flex items-center gap-3">
						<div className="p-2 rounded-full bg-accent/10">
							<XCircle className="w-5 h-5 text-destructive" />
						</div>
						<div className="flex flex-col gap-0.5">
							<p className="font-medium">RSVP declined</p>
							<p className="text-sm text-white/80">
								You can't make it to this event
							</p>
						</div>
					</div>,
					{ icon: null },
				);
			}
		},
		onError: () => {
			toast.error(
				<div className="flex flex-col gap-1">
					<p className="font-medium">Oops! Something went wrong</p>
					<p className="text-sm opacity-80">Please try again</p>
				</div>,
			);
		},
	});

	return {
		submitRsvp,
		isSubmitting: isPending,
	};
}
